import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, ShoppingCart, X, Plus, Minus, FileText, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import ContractText from "@/components/site/ContractText";
import { CONTRACT_VERSION } from "@/lib/contract";

export default function Pricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { lookup_key: quantity }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", property_address: "" });
  const [agreed, setAgreed] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const contractScrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing");
        setPricing(data);
      } catch (e) {
        toast.error("Couldn't load pricing.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Automatic rate selection — Early until Oct 1, then Regular
  const rateMode = pricing?.early_active ? "early" : "regular";

  const addItem = (lookup_key) => {
    setCart((c) => ({ ...c, [lookup_key]: (c[lookup_key] || 0) + 1 }));
    toast.success("Added to cart");
    if (!drawerOpen) setDrawerOpen(true);
  };
  const removeItem = (lookup_key) => {
    setCart((c) => {
      const next = { ...c };
      delete next[lookup_key];
      return next;
    });
  };
  const changeQty = (lookup_key, delta) => {
    setCart((c) => {
      const next = { ...c };
      const q = (next[lookup_key] || 0) + delta;
      if (q <= 0) delete next[lookup_key];
      else next[lookup_key] = q;
      return next;
    });
  };

  const lookupToItem = useMemo(() => {
    const map = {};
    if (!pricing) return map;
    pricing.seasonal.forEach((s) => {
      map[s.regular_lookup_key] = { label: `${s.label} — Seasonal (Regular)`, amount: s.regular_amount };
      map[s.early_lookup_key] = { label: `${s.label} — Seasonal (Early)`, amount: s.early_amount };
    });
    pricing.addons.forEach((a) => {
      map[a.lookup_key] = { label: `${a.label} (${a.unit})`, amount: a.amount };
    });
    pricing.visit.forEach((v) => {
      map[v.lookup_key] = { label: `${v.label} — Pay-Per-Visit`, amount: v.amount };
    });
    return map;
  }, [pricing]);

  const cartItems = Object.entries(cart).map(([k, q]) => ({
    lookup_key: k, quantity: q, ...lookupToItem[k],
  }));
  const cartCount = cartItems.reduce((n, i) => n + i.quantity, 0);
  const cartSubtotal = cartItems.reduce((n, i) => n + (i.amount || 0) * i.quantity, 0);

  const hasSeasonalInCart = cartItems.some((i) => i.lookup_key.startsWith("seasonal_"));
  const isAfterNov15 = (() => {
    const now = new Date();
    return now.getMonth() > 10 || (now.getMonth() === 10 && now.getDate() > 15);
  })();
  const addonsToShow = pricing
    ? pricing.addons.filter((a) => {
        if (a.lookup_key === "addon_late_signup") return isAfterNov15;
        return true;
      })
    : [];

  const startCheckout = async () => {
    if (cartCount === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    if (!customer.name || !customer.email || !customer.phone || !customer.property_address) {
      toast.error("Please provide name, email, phone, and property address.");
      return;
    }
    if (!agreed) {
      toast.error("Please read and accept the Service Agreement to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: cartItems.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: customer.email,
        customer_name: customer.name,
        customer_phone: customer.phone,
        property_address: customer.property_address,
        contract_version: CONTRACT_VERSION,
        contract_agreed: true,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setSubmitting(false);
    }
  };

  const onContractScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setReachedEnd(true);
    }
  };

  if (loading) {
    return (
      <section id="pricing" className="py-24 md:py-32 text-center">
        <div className="hairline">Loading pricing…</div>
      </section>
    );
  }

  if (!pricing) return null;

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="hairline mb-6">Book Online</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-2xl">
              Lock in your driveway.
              <span className="text-[#C9A24B]"> Pay in seconds.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {pricing.early_active && (
              <div
                data-testid="early-rate-badge"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-[#C9A24B]/50 bg-[#C9A24B]/10 text-[#C9A24B] text-[11px] font-semibold uppercase tracking-[0.18em]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#C9A24B] animate-pulse" />
                Early Rate · Ends Oct 1
              </div>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="open-cart"
              className="relative inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 hover:border-[#C9A24B] hover:text-[#C9A24B] text-xs font-semibold uppercase tracking-[0.18em] transition-colors"
            >
              <ShoppingCart size={14} /> Cart
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-2 -right-2 h-5 min-w-5 px-1.5 rounded-full bg-[#C9A24B] text-[#050B14] text-[10px] font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Seasonal grid */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.25em] text-[#9BD0E8] font-semibold mb-4">
            Seasonal Driveway Contracts
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {pricing.seasonal.map((tier) => {
              const isEarly = rateMode === "early";
              const amount = isEarly ? tier.early_amount : tier.regular_amount;
              const lookup = isEarly ? tier.early_lookup_key : tier.regular_lookup_key;
              const savings = isEarly && tier.regular_amount && tier.early_amount
                ? tier.regular_amount - tier.early_amount
                : 0;
              return (
                <div
                  key={tier.key}
                  data-testid={`tier-${tier.key}`}
                  className="relative bg-[#0B1B3D] hover:bg-[#0F2554] transition-colors p-6 flex flex-col"
                >
                  {savings > 0 && (
                    <div
                      data-testid={`tier-save-${tier.key}`}
                      className="absolute top-4 right-4 bg-[#C9A24B] text-[#050B14] text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1"
                    >
                      Save ${savings.toFixed(0)}
                    </div>
                  )}
                  <div className="font-display text-xl font-semibold mb-2">{tier.label}</div>
                  <div className="text-xs text-white/60 mb-5 leading-relaxed">{tier.desc}</div>
                  <div className="mt-auto">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
                      {isEarly ? "Early Rate" : "Regular Rate"}
                    </div>
                    <div className="flex items-baseline gap-3 mb-4">
                      <div className="font-display text-3xl font-bold text-[#C9A24B]">
                        ${amount?.toFixed(0)}
                      </div>
                      {isEarly && tier.regular_amount != null && (
                        <div
                          data-testid={`tier-regular-${tier.key}`}
                          className="font-display text-lg text-white/40 line-through decoration-white/30"
                        >
                          ${tier.regular_amount.toFixed(0)}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-white/50 mb-4 -mt-2">CAD / season</div>
                    <button
                      onClick={() => addItem(lookup)}
                      data-testid={`add-tier-${tier.key}`}
                      className="w-full inline-flex items-center justify-center gap-2 py-3 bg-[#C9A24B] hover:bg-[#E0B962] text-[#050B14] text-xs font-semibold uppercase tracking-[0.18em] transition-colors"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
            <Sparkles size={12} className="text-[#C9A24B]" />
            <span>{pricing.addons.length} optional add-ons available when you check out — walkway, salting, garage hand-finish and more.</span>
          </div>
        </div>

        {/* Pay-per-visit only */}
        <div className="mt-10 bg-[#0B1B3D] border border-white/10 p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#9BD0E8] font-semibold mb-2">
            Pay-Per-Visit
          </div>
          <div className="text-xs text-white/55 mb-6">
            Don't want to commit to a season? Book a single visit.
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {pricing.visit.map((v) => (
              <li key={v.lookup_key} data-testid={`visit-${v.lookup_key}`} className="bg-[#0B1B3D] p-5 flex flex-col items-start gap-3">
                <div className="text-sm font-medium text-white">{v.label}</div>
                <div className="font-display text-2xl font-bold text-[#C9A24B]">${v.amount?.toFixed(0)}</div>
                <button
                  onClick={() => addItem(v.lookup_key)}
                  data-testid={`add-${v.lookup_key}`}
                  className="mt-auto w-full inline-flex items-center justify-center gap-2 py-2.5 border border-white/20 hover:border-[#C9A24B] hover:text-[#C9A24B] text-xs font-semibold uppercase tracking-[0.18em] transition-colors"
                >
                  <Plus size={12} /> Add
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Cart drawer */}
      {drawerOpen && (
        <div
          data-testid="cart-drawer"
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex justify-end"
          onClick={() => setDrawerOpen(false)}
        >
          <aside
            className="w-full md:w-[440px] h-full bg-[#0B1B3D] border-l border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="font-display text-xl font-bold">Your Cart</div>
              <button onClick={() => setDrawerOpen(false)} data-testid="close-cart" className="text-white/60 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {cartItems.length === 0 ? (
                <div className="text-center text-white/50 text-sm py-16">Your cart is empty.</div>
              ) : (
                <ul className="space-y-4">
                  {cartItems.map((i) => (
                    <li key={i.lookup_key} className="border border-white/10 p-4" data-testid={`cart-item-${i.lookup_key}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white leading-snug">{i.label}</div>
                          <div className="text-xs text-white/50 mt-1">${i.amount?.toFixed(2)} CAD each</div>
                        </div>
                        <button onClick={() => removeItem(i.lookup_key)} data-testid={`cart-remove-${i.lookup_key}`} className="text-white/40 hover:text-red-400" aria-label="Remove">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="inline-flex items-center border border-white/15">
                          <button onClick={() => changeQty(i.lookup_key, -1)} data-testid={`cart-dec-${i.lookup_key}`} className="w-8 h-8 flex items-center justify-center hover:text-[#C9A24B]">
                            <Minus size={12} />
                          </button>
                          <div className="w-8 h-8 flex items-center justify-center text-sm">{i.quantity}</div>
                          <button onClick={() => changeQty(i.lookup_key, 1)} data-testid={`cart-inc-${i.lookup_key}`} className="w-8 h-8 flex items-center justify-center hover:text-[#C9A24B]">
                            <Plus size={12} />
                          </button>
                        </div>
                        <div className="font-display text-base font-bold text-[#C9A24B]">
                          ${((i.amount || 0) * i.quantity).toFixed(2)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {hasSeasonalInCart && addonsToShow.length > 0 && (
                <div data-testid="upsell-block" className="mt-8">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={13} className="text-[#C9A24B]" />
                    <div className="text-xs uppercase tracking-[0.22em] text-[#C9A24B] font-semibold">
                      Complete your service
                    </div>
                  </div>
                  <div className="text-[11px] text-white/50 mb-4">
                    Recommended add-ons for your seasonal contract.
                  </div>
                  <ul className="space-y-2">
                    {addonsToShow.map((a) => {
                      const inCart = (cart[a.lookup_key] || 0) > 0;
                      return (
                        <li
                          key={a.lookup_key}
                          data-testid={`upsell-${a.lookup_key}`}
                          className={`flex items-center justify-between gap-3 border p-3 transition-colors ${
                            inCart ? "border-[#C9A24B]/40 bg-[#C9A24B]/5" : "border-white/10 hover:border-white/25"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm text-white font-medium leading-tight">{a.label}</div>
                            <div className="text-[11px] text-white/50 mt-0.5">
                              ${a.amount?.toFixed(0)} · {a.unit}
                              {inCart ? ` · ${cart[a.lookup_key]} in cart` : ""}
                            </div>
                          </div>
                          <button
                            onClick={() => addItem(a.lookup_key)}
                            data-testid={`upsell-add-${a.lookup_key}`}
                            className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                              inCart
                                ? "text-[#C9A24B] hover:text-[#E0B962]"
                                : "bg-[#C9A24B]/10 text-[#C9A24B] hover:bg-[#C9A24B] hover:text-[#050B14]"
                            }`}
                          >
                            <Plus size={11} /> {inCart ? "Add more" : "Add"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-white/10">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">Subtotal</div>
                <div className="font-display text-2xl font-bold text-[#C9A24B]" data-testid="cart-subtotal">
                  ${cartSubtotal.toFixed(2)} CAD
                </div>
              </div>
              <div className="text-[11px] text-white/45 mb-5">
                Taxes calculated at checkout. Secure payment via Stripe.
              </div>
              <button
                onClick={() => setCheckoutOpen(true)}
                disabled={cartItems.length === 0}
                data-testid="proceed-checkout"
                className="w-full inline-flex items-center justify-center gap-2 py-4 bg-[#C9A24B] hover:bg-[#E0B962] disabled:opacity-40 disabled:cursor-not-allowed text-[#050B14] text-sm font-semibold tracking-wide transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Customer details + Service Agreement modal */}
      {checkoutOpen && (
        <div
          data-testid="checkout-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !submitting && setCheckoutOpen(false)}
        >
          <div
            className="w-full max-w-2xl bg-[#0B1B3D] border border-white/10 my-8 max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B] font-semibold mb-1">Step 1 of 2</div>
                <div className="font-display text-xl font-bold">Almost there</div>
              </div>
              <button onClick={() => !submitting && setCheckoutOpen(false)} data-testid="close-checkout" className="text-white/60 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="p-6 md:p-8">
                <p className="text-sm text-white/65 mb-6">
                  A few details so we know where to plow. You'll pay securely on Stripe in the next step.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">Full name</Label>
                    <Input id="c-name" data-testid="checkout-name" value={customer.name}
                      onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      className="bg-[#050B14] border-white/15 rounded-none h-11 focus-visible:ring-[#C9A24B]" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-email" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">Email</Label>
                    <Input id="c-email" type="email" data-testid="checkout-email" value={customer.email}
                      onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      className="bg-[#050B14] border-white/15 rounded-none h-11 focus-visible:ring-[#C9A24B]" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-phone" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">Mobile phone (SMS dispatch alerts)</Label>
                    <Input id="c-phone" data-testid="checkout-phone" value={customer.phone}
                      onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                      placeholder="(613) 555-0123"
                      className="bg-[#050B14] border-white/15 rounded-none h-11 focus-visible:ring-[#C9A24B]" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-addr" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">Property address</Label>
                    <Input id="c-addr" data-testid="checkout-address" value={customer.property_address}
                      onChange={(e) => setCustomer({ ...customer, property_address: e.target.value })}
                      placeholder="123 Bank St, Ottawa"
                      className="bg-[#050B14] border-white/15 rounded-none h-11 focus-visible:ring-[#C9A24B]" />
                  </div>
                </div>
              </div>

              <div className="px-6 md:px-8 pb-8">
                <div className="mt-4 mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">
                  <FileText size={13} /> Service Agreement · Please review
                </div>

                <div
                  ref={contractScrollRef}
                  onScroll={onContractScroll}
                  data-testid="contract-scroll"
                  className="max-h-[280px] overflow-y-auto bg-[#050B14] border border-white/10 p-5 md:p-6 mb-4 scroll-smooth"
                >
                  <ContractText compact />
                  {!reachedEnd && (
                    <div className="text-center text-[11px] text-[#C9A24B] mt-4 animate-pulse">
                      ↓ Scroll to the end to enable acceptance
                    </div>
                  )}
                </div>

                <label
                  className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                    agreed ? "border-[#C9A24B] bg-[#C9A24B]/5" : "border-white/15 hover:border-white/30"
                  } ${!reachedEnd ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Checkbox
                    checked={agreed}
                    disabled={!reachedEnd}
                    onCheckedChange={(v) => setAgreed(!!v)}
                    data-testid="contract-agree"
                    className="mt-0.5 border-white/40 data-[state=checked]:bg-[#C9A24B] data-[state=checked]:border-[#C9A24B]"
                  />
                  <div className="text-sm text-white/85 leading-relaxed">
                    <span className="font-semibold text-white">I agree to the Terms & Conditions</span>
                    <div className="text-xs text-white/55 mt-1">
                      I acknowledge I have read, understood, and agree to be bound by the Residential Snow Removal Service Agreement (Version {CONTRACT_VERSION}).
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex-shrink-0 bg-[#0B1B3D]">
              <button
                onClick={startCheckout}
                disabled={submitting || !agreed}
                data-testid="start-stripe-checkout"
                className="w-full inline-flex items-center justify-center gap-2 py-4 bg-[#C9A24B] hover:bg-[#E0B962] disabled:opacity-40 disabled:cursor-not-allowed text-[#050B14] text-sm font-semibold tracking-wide transition-colors"
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> Redirecting to Stripe…</>
                ) : (
                  <>Pay ${cartSubtotal.toFixed(2)} CAD securely</>
                )}
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-white/40">
                <Check size={12} /> Secured by Stripe · 256-bit encryption
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
